import type { AssessmentQuestion, Sector } from '../../lib/assessment/types'
import { TRI_OPTIONS } from './companyProfile'

const tri = TRI_OPTIONS

export const REPORTING_QUESTIONS: AssessmentQuestion[] = [
  {
    id: 'rc_listed_exchange',
    section: 'reporting',
    type: 'single',
    label: 'Is the company listed on ADX or DFM?',
    helpText: 'Select based on current listing status. This is a company fact, not a legal conclusion.',
    required: true,
    options: [
      { value: 'adx', label: 'Yes — ADX' },
      { value: 'dfm', label: 'Yes — DFM' },
      { value: 'both', label: 'Yes — both' },
      { value: 'no', label: 'No' },
      { value: 'not_sure', label: 'Not sure' },
    ],
  },
  {
    id: 'rc_ipo',
    section: 'reporting',
    type: 'single',
    label: 'Is the company preparing for an IPO?',
    required: true,
    options: tri,
  },
  {
    id: 'rc_customer_requests',
    section: 'reporting',
    type: 'single',
    label: 'Does the company receive sustainability or ESG data requests from customers?',
    required: true,
    options: tri,
  },
  {
    id: 'rc_multinational_supply',
    section: 'reporting',
    type: 'single',
    label: 'Does the company supply multinational businesses that request ESG information?',
    required: true,
    options: tri,
  },
  {
    id: 'rc_export_eu',
    section: 'reporting',
    type: 'single',
    label: 'Does the company export goods to the EU or other markets with carbon-related disclosure expectations?',
    helpText: 'This captures market exposure (for example EU CBAM-related customer questions). It is not a UAE legal determination.',
    required: true,
    options: tri,
  },
  {
    id: 'rc_contractual_emissions',
    section: 'reporting',
    type: 'single',
    label: 'Does the company have contractual emissions-reporting commitments?',
    required: true,
    options: tri,
  },
  {
    id: 'rc_authority_notice',
    section: 'reporting',
    type: 'single',
    label: 'Has the company received a specific reporting notice from an authority?',
    helpText: 'If unsure, choose Not sure. Do not treat silence as proof of non-applicability.',
    required: true,
    options: tri,
  },
  {
    id: 'rc_designated_mrv',
    section: 'reporting',
    type: 'single',
    label:
      'Has a UAE ministry or competent authority designated or notified the company for emissions measurement, inventory or periodic reporting?',
    helpText:
      'Under Federal Decree-Law No. 11 of 2024, detailed measurement/inventory/reporting duties are linked to sources determined by the Ministry and competent authority. This tool cannot confirm designation status.',
    required: true,
    options: tri,
  },
  {
    id: 'rc_has_ghg_inventory',
    section: 'reporting',
    type: 'single',
    label: 'Does the company already prepare a GHG inventory?',
    required: true,
    options: tri,
  },
  {
    id: 'rc_framework',
    section: 'reporting',
    type: 'single',
    label: 'Does the company follow a reporting framework such as GRI, IFRS Sustainability Disclosure Standards, or another framework?',
    required: true,
    options: [
      { value: 'gri', label: 'Yes — GRI' },
      { value: 'ifrs', label: 'Yes — IFRS Sustainability Disclosure Standards' },
      { value: 'other', label: 'Yes — other framework' },
      { value: 'multiple', label: 'Yes — more than one' },
      { value: 'no', label: 'No formal framework yet' },
      { value: 'not_sure', label: 'Not sure' },
    ],
  },
  {
    id: 'rc_multi_emirate',
    section: 'reporting',
    type: 'single',
    label: 'Does the company have operations in more than one emirate?',
    required: true,
    options: tri,
  },
  {
    id: 'rc_parent_reporting',
    section: 'reporting',
    type: 'single',
    label: 'Does a parent company require the UAE entity to supply sustainability or GHG data for group reporting?',
    required: true,
    options: tri,
  },
  {
    id: 'rc_assurance_interest',
    section: 'reporting',
    type: 'single',
    label: 'Is the company preparing for external assurance or formal review of sustainability/GHG data?',
    required: false,
    options: tri,
  },
]

/** Sector-specific readiness probes shown in the carbon step. */
export const SECTOR_BRANCH_QUESTIONS: AssessmentQuestion[] = [
  {
    id: 'sec_mfg_process_data',
    section: 'sector',
    type: 'single',
    label: 'Are production volumes and process activity data available for the reporting period?',
    sectorInclude: ['manufacturing'],
    required: true,
    options: tri,
  },
  {
    id: 'sec_mfg_raw_materials',
    section: 'sector',
    type: 'single',
    label: 'Can purchased raw-material quantities or spend be linked to major product lines?',
    sectorInclude: ['manufacturing'],
    required: true,
    options: tri,
  },
  {
    id: 'sec_con_embodied',
    section: 'sector',
    type: 'single',
    label: 'Do you collect embodied-carbon or EPD data for major construction materials?',
    sectorInclude: ['construction'],
    required: true,
    options: tri,
  },
  {
    id: 'sec_con_subcontractors',
    section: 'sector',
    type: 'single',
    label: 'Are subcontractor fuel/energy activities identified in your boundary approach?',
    helpText: 'Missing subcontractor data is a readiness gap — not evidence the activity is zero.',
    sectorInclude: ['construction'],
    required: true,
    options: tri,
  },
  {
    id: 'sec_re_tenant_data',
    section: 'sector',
    type: 'single',
    label: 'For managed buildings, is tenant versus landlord energy data clearly separated?',
    sectorInclude: ['real_estate'],
    required: true,
    options: tri,
  },
  {
    id: 'sec_re_portfolio_coverage',
    section: 'sector',
    type: 'single',
    label: 'Is energy data available for the full in-scope building portfolio?',
    sectorInclude: ['real_estate'],
    required: true,
    options: [
      { value: 'full', label: 'Yes — full portfolio' },
      { value: 'partial', label: 'Partial portfolio only' },
      { value: 'no', label: 'No' },
      { value: 'not_sure', label: 'Not sure' },
    ],
  },
  {
    id: 'sec_log_ownership',
    section: 'sector',
    type: 'single',
    label: 'Is vehicle ownership/control clearly distinguished from outsourced transport?',
    sectorInclude: ['logistics'],
    required: true,
    options: tri,
  },
  {
    id: 'sec_log_utilization',
    section: 'sector',
    type: 'single',
    label: 'Do you track empty running or transport utilization data where relevant?',
    sectorInclude: ['logistics'],
    required: false,
    options: tri,
  },
  {
    id: 'sec_hosp_food_waste',
    section: 'sector',
    type: 'single',
    label: 'Is food-waste data collected for kitchens / F&B operations?',
    sectorInclude: ['hospitality'],
    required: true,
    options: tri,
  },
  {
    id: 'sec_hosp_laundry',
    section: 'sector',
    type: 'single',
    label: 'Are laundry and outsourced housekeeping energy/water impacts identified?',
    sectorInclude: ['hospitality'],
    required: false,
    options: tri,
  },
  {
    id: 'sec_fin_financed',
    section: 'sector',
    type: 'single',
    label: 'Has the firm screened whether financed-emissions / investment portfolio data may be relevant?',
    helpText: 'Screening relevance is not the same as calculating financed emissions.',
    sectorInclude: ['financial_services'],
    required: true,
    options: tri,
  },
  {
    id: 'sec_tech_cloud',
    section: 'sector',
    type: 'single',
    label: 'Are cloud / data-centre supplier energy or GHG reports available for screening?',
    sectorInclude: ['technology_services'],
    required: true,
    options: tri,
  },
  {
    id: 'sec_og_flaring',
    section: 'sector',
    type: 'single',
    label: 'Are flaring, venting or process-release sources inventoried where applicable?',
    sectorInclude: ['oil_gas_energy'],
    required: true,
    options: tri,
  },
  {
    id: 'sec_hc_clinical_waste',
    section: 'sector',
    type: 'single',
    label: 'Are clinical / hazardous waste streams tracked separately from general waste?',
    sectorInclude: ['healthcare'],
    required: true,
    options: tri,
  },
  {
    id: 'sec_ag_land',
    section: 'sector',
    type: 'single',
    label: 'Are land-use, fertilizer or livestock-related emission sources screened where applicable?',
    sectorInclude: ['agriculture'],
    required: true,
    options: tri,
  },
  {
    id: 'sec_retail_cold_chain',
    section: 'sector',
    type: 'single',
    label: 'Is cold-chain / refrigeration equipment inventoried across stores or DCs?',
    sectorInclude: ['retail'],
    required: true,
    options: tri,
  },
]

export const EVIDENCE_QUESTIONS: AssessmentQuestion[] = [
  {
    id: 'ev_source_documents',
    section: 'evidence',
    type: 'readiness',
    label: 'Activity data is supported by source documents (bills, invoices, meter logs).',
    required: true,
  },
  {
    id: 'ev_consistent_collection',
    section: 'evidence',
    type: 'readiness',
    label: 'Data is collected consistently across in-scope facilities.',
    required: true,
  },
  {
    id: 'ev_methods_documented',
    section: 'evidence',
    type: 'readiness',
    label: 'Calculation methods are documented.',
    required: true,
  },
  {
    id: 'ev_emission_factors',
    section: 'evidence',
    type: 'readiness',
    label: 'Emission factors have identifiable sources and versions.',
    required: true,
  },
  {
    id: 'ev_review',
    section: 'evidence',
    type: 'readiness',
    label: 'Data is reviewed before internal or external reporting.',
    required: true,
  },
  {
    id: 'ev_estimates',
    section: 'evidence',
    type: 'readiness',
    label: 'Corrections and estimates are documented.',
    required: true,
  },
  {
    id: 'ev_boundary',
    section: 'evidence',
    type: 'readiness',
    label: 'Organizational and operational reporting boundaries are defined.',
    required: true,
  },
  {
    id: 'ev_exclusions',
    section: 'evidence',
    type: 'readiness',
    label: 'Exclusions have documented explanations.',
    required: true,
  },
  {
    id: 'ev_owners',
    section: 'evidence',
    type: 'readiness',
    label: 'Data ownership and responsibilities are assigned.',
    required: true,
  },
  {
    id: 'ev_base_year',
    section: 'evidence',
    type: 'readiness',
    label: 'A base year has been selected where relevant, with rationale documented.',
    required: false,
  },
  {
    id: 'ev_method_changes',
    section: 'evidence',
    type: 'readiness',
    label: 'Changes to methods or organizational boundaries are tracked.',
    required: false,
  },
  {
    id: 'ev_retrieval',
    section: 'evidence',
    type: 'readiness',
    label: 'Records can be retrieved for review within a reasonable time.',
    required: true,
  },
]

export const STAKEHOLDER_QUESTIONS: AssessmentQuestion[] = [
  {
    id: 'st_customers',
    section: 'stakeholders',
    type: 'single',
    label: 'Customers request ESG / carbon information',
    required: true,
    options: [
      { value: 'frequent', label: 'Yes — frequently' },
      { value: 'occasional', label: 'Yes — occasionally' },
      { value: 'anticipated', label: 'Not yet, but anticipated' },
      { value: 'no', label: 'No' },
      { value: 'not_sure', label: 'Not sure' },
    ],
  },
  {
    id: 'st_suppliers',
    section: 'stakeholders',
    type: 'single',
    label: 'Suppliers are asked to provide ESG / carbon information to your company',
    required: false,
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'partial', label: 'For some suppliers only' },
      { value: 'no', label: 'No' },
      { value: 'not_sure', label: 'Not sure' },
    ],
  },
  {
    id: 'st_lenders',
    section: 'stakeholders',
    type: 'single',
    label: 'Lenders or banks request ESG information',
    required: true,
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'anticipated', label: 'Anticipated' },
      { value: 'no', label: 'No' },
      { value: 'not_sure', label: 'Not sure' },
    ],
  },
  {
    id: 'st_investors',
    section: 'stakeholders',
    type: 'single',
    label: 'Investors or parent company request ESG information',
    required: true,
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'anticipated', label: 'Anticipated' },
      { value: 'no', label: 'No' },
      { value: 'not_sure', label: 'Not sure' },
    ],
  },
  {
    id: 'st_regulators',
    section: 'stakeholders',
    type: 'single',
    label: 'Regulators or authorities have requested sustainability information',
    required: true,
    options: tri,
  },
  {
    id: 'st_employees',
    section: 'stakeholders',
    type: 'single',
    label: 'Employees or works councils request sustainability information',
    required: false,
    options: tri,
  },
  {
    id: 'st_certification',
    section: 'stakeholders',
    type: 'single',
    label: 'Certification bodies require ESG / environmental evidence',
    required: false,
    options: tri,
  },
  {
    id: 'st_info_types',
    section: 'stakeholders',
    type: 'single',
    label: 'What information is most often requested?',
    required: false,
    showWhen: [{ questionId: 'st_customers', equals: ['frequent', 'occasional', 'anticipated'] }],
    options: [
      { value: 'ghg', label: 'GHG / carbon figures' },
      { value: 'policies', label: 'Policies and governance' },
      { value: 'both', label: 'Both carbon and broader ESG' },
      { value: 'other', label: 'Other / varies' },
      { value: 'not_sure', label: 'Not sure' },
    ],
  },
  {
    id: 'st_can_respond',
    section: 'stakeholders',
    type: 'single',
    label: 'Can the company respond reliably to current ESG information requests?',
    required: true,
    options: [
      { value: 'yes', label: 'Yes — reliably' },
      { value: 'partial', label: 'Only partially' },
      { value: 'no', label: 'No' },
      { value: 'not_applicable', label: 'No active requests' },
      { value: 'not_sure', label: 'Not sure' },
    ],
  },
]

export function sectorBranchQuestionsFor(sector: Sector | ''): AssessmentQuestion[] {
  return SECTOR_BRANCH_QUESTIONS.filter((q) => {
    if (!q.sectorInclude?.length) return true
    if (!sector) return false
    return q.sectorInclude.includes(sector)
  })
}

export const ALL_STATIC_QUESTIONS: AssessmentQuestion[] = [
  ...REPORTING_QUESTIONS,
  ...SECTOR_BRANCH_QUESTIONS,
  ...EVIDENCE_QUESTIONS,
  ...STAKEHOLDER_QUESTIONS,
]
